import classNames from "classnames";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useMatch, useNavigate } from "react-router-dom";
import ActionDropdown, { MenuSize } from "~@/components/layout/ActionDropdown";
import Avatar from "~@/components/layout/Avatar";
import {
  OrganizationStatus,
  useGetOrganizationsQuery,
} from "~@/graphql/codegen/generated";
import useExitApp from "~@/hooks/useExitApp";
import useInternalUser from "~@/hooks/useInternalUser";
import useSwitchOrganization from "~@/hooks/useSwitchOrganization";
import {
  ArrowCircleRight,
  ArrowLeft14x14Icon,
  ArrowRight14x14Icon,
  ChevronDown12x16Icon,
  SpreadedDotsIcon,
} from "~@/icons";
import OrganizationStatusBadge, {
  organizationStatusBadgeProps,
} from "~@/pages/multi-account/components/OrganizationStatus";

const Header = () => {
  const match = useMatch("/multi-account");

  const { internalUser, isInternalUserLoading } = useInternalUser();

  const { data: organizationsData, loading: isOrganizationsLoading } =
    useGetOrganizationsQuery({
      variables: {
        statuses: [
          OrganizationStatus.Active,
          OrganizationStatus.Inactive,
          OrganizationStatus.Draft,
        ],
        isWithInvoices: false,
        isDeleted: false,
      },
    });

  const isInOrganization = useMemo(() => {
    if (!organizationsData || !organizationsData.getOrganizations) return false;
    return organizationsData.getOrganizations.current;
  }, [organizationsData]);

  const switchOrganization = useSwitchOrganization();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const exitApp = useExitApp();

  if (!isInternalUserLoading && !internalUser) return null;
  return (
    <nav className="header navbar">
      <div className="navbar__menu">
        <div className="navbar__start">
          <div className="navbar__item">
            {isInOrganization ? (
              <div className="buttons buttons--has-gap-3">
                {!organizationsData?.getOrganizations?.current
                  ?.parentOID ? null : (
                  <button
                    onClick={() => navigate(match ? "/" : "/multi-account")}
                    className="button button--is-fill-gray-5 button--h-36"
                  >
                    <span className="icon-text">
                      <span className="icon icon--14x14">
                        <ArrowLeft14x14Icon />
                      </span>
                      <span>
                        {t(
                          match
                            ? "multi-account.platform"
                            : "multi-account.admin-panel"
                        )}
                      </span>
                      <span className="icon icon--19x19">
                        <SpreadedDotsIcon />
                      </span>
                    </span>
                  </button>
                )}
                <ActionDropdown
                  menuSize={MenuSize.Medium}
                  hasOverlay={false}
                  contentStyles={{ maxHeight: 206, overflowX: "hidden" }}
                  className="action-dropdown--has-gap-2"
                  popperOptions={{
                    placement: "bottom-start",
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  }}
                  triggerElement={({ toggleDropdown, isDropdownOpen }) => (
                    <button
                      className={classNames(
                        "button button--is-fill-gray-5 button--h-36 multi-account__button",
                        { "multi-account__button--is-open": isDropdownOpen }
                      )}
                      type="button"
                      onClick={toggleDropdown}
                    >
                      <div className="icon-text">
                        <span>{t("multi-account.switch")}</span>
                        <span className="icon icon--has-default-size">
                          <ChevronDown12x16Icon />
                        </span>
                      </div>
                    </button>
                  )}
                  content={() =>
                    organizationsData?.getOrganizations?.subsidiaries.map(
                      (organization) => {
                        if (!organization) return null;
                        return (
                          <div
                            role="presentation"
                            onClick={() => {
                              if (organization.isCurrent) return;

                              return switchOrganization(organization.id);
                            }}
                            key={organization.id}
                            className="dropdown-organization-card"
                          >
                            <Avatar
                              mediaParams={{
                                url: organization.imageUrl as string,
                                width: 52,
                                height: 52,
                                quality: 100,
                                upscale: true,
                              }}
                            />
                            {organization.isCurrent ? (
                              <div className="dropdown-organization-card__info dropdown-organization-card__info--is-current">
                                <div className="dropdown-organization-card__title dropdown-organization-card__title--is-current">
                                  <p>{organization.name}</p>
                                  <span>
                                    {t("multi-account.currently-into")}
                                  </span>
                                </div>
                                <OrganizationStatusBadge
                                  {...organizationStatusBadgeProps(organization)}
                                />
                              </div>
                            ) : (
                              <div className="dropdown-organization-card__info">
                                <p className="dropdown-organization-card__title">
                                  {organization.name}
                                </p>
                                <OrganizationStatusBadge
                                  {...organizationStatusBadgeProps(organization)}
                                />
                              </div>
                            )}
                            {!organization.isCurrent ? (
                              <button className="dropdown-organization-card__button">
                                <span className="icon icon--is-14x14 icon--is-gray-5">
                                  <ArrowRight14x14Icon />
                                </span>
                              </button>
                            ) : null}
                          </div>
                        );
                      }
                    )
                  }
                />
              </div>
            ) : isOrganizationsLoading ? (
              <Skeleton
                height={35}
                width={163}
                baseColor="#333333"
                highlightColor="#999999"
                wrapper={({ children }) => (
                  <div style={{ lineHeight: 1, borderRadius: 6 }}>
                    {children}
                  </div>
                )}
              />
            ) : (
              <ActionDropdown
                menuSize={MenuSize.Medium}
                hasOverlay={false}
                contentStyles={{ maxHeight: 206 }}
                style={{
                  marginRight: 8,
                }}
                className="action-dropdown--has-gap-2"
                popperOptions={{
                  placement: "bottom-start",
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 6],
                      },
                    },
                  ],
                }}
                triggerElement={({ toggleDropdown, isDropdownOpen }) => (
                  <button
                    className="button button--is-fill-gray-5 button--h-36"
                    type="button"
                    onClick={toggleDropdown}
                  >
                    <div className="icon-text">
                      <span>{t("multi-account.go-to-account")}</span>
                      <span className="icon icon--has-default-size">
                        <ChevronDown12x16Icon />
                      </span>
                    </div>
                  </button>
                )}
                content={() =>
                  organizationsData?.getOrganizations?.subsidiaries.map(
                    (organization) => {
                      if (!organization) return null;
                      return (
                        <div
                          role="presentation"
                          onClick={() => {
                            if (organization.isCurrent) return;

                            return switchOrganization(organization.id);
                          }}
                          key={organization.id}
                          className="dropdown-organization-card"
                        >
                          <Avatar
                            mediaParams={{
                              url: organization.imageUrl as string,
                              width: 52,
                              height: 52,
                              quality: 100,
                              upscale: true,
                            }}
                          />
                          {organization.isCurrent ? (
                            <div className="dropdown-organization-card__info dropdown-organization-card__info--is-current">
                              <div className="dropdown-organization-card__title dropdown-organization-card__title--is-current">
                                <p> {organization.name}</p>
                                <span>{t("multi-account.currently-into")}</span>
                              </div>
                              <OrganizationStatusBadge
                                {...organizationStatusBadgeProps(organization)}
                              />
                            </div>
                          ) : (
                            <div className="dropdown-organization-card__info">
                              <p className="dropdown-organization-card__title">
                                {organization.name}
                              </p>
                              <OrganizationStatusBadge
                                {...organizationStatusBadgeProps(organization)}
                              />
                            </div>
                          )}
                          {!organization.isCurrent ? (
                            <button className="dropdown-organization-card__button">
                              <span className="icon icon--is-14x14 icon--is-gray-5">
                                <ArrowRight14x14Icon />
                              </span>
                            </button>
                          ) : null}
                        </div>
                      );
                    }
                  )
                }
              />
            )}
          </div>
        </div>
        <div className="navbar__center">
          <div className="navbar__item">
            {isInOrganization ? (
              <div className="header__organization-badge">
                <h1
                  className="header__organization-title"
                  title={organizationsData?.getOrganizations?.current?.name}
                >
                  {organizationsData?.getOrganizations?.current?.name}
                </h1>
                {organizationsData?.getOrganizations?.current ? (
                  <OrganizationStatusBadge
                    {...organizationStatusBadgeProps(
                      organizationsData.getOrganizations.current
                    )}
                  />
                ) : null}
              </div>
            ) : isOrganizationsLoading ? (
              <Skeleton
                height={48}
                width={300}
                baseColor="#333333"
                highlightColor="#999999"
                wrapper={({ children }) => (
                  <div style={{ lineHeight: 1, borderRadius: 8 }}>
                    {children}
                  </div>
                )}
              />
            ) : (
              <div className="icon-text header__organization-placeholder">
                <span className="icon">
                  <SpreadedDotsIcon />
                </span>
                <span>{t("multi-account.acc-administration")}</span>
              </div>
            )}
          </div>
        </div>
        <div className="navbar__end">
          <div className="navbar__item">
            {isOrganizationsLoading ? (
              <Skeleton
                height={36}
                width={100}
                baseColor="#333333"
                highlightColor="#999999"
                wrapper={({ children }) => (
                  <div style={{ lineHeight: 1, borderRadius: 8 }}>
                    {children}
                  </div>
                )}
              />
            ) : (
              <button
                className="button button--is-fill-gray-5 button--h-36"
                type="button"
                onClick={exitApp}
              >
                <span>{t("user-dropdown.exit")}</span>
                <span className="icon icon--is-18x18">
                  <ArrowCircleRight />
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
